import { useMemo, useState } from "react";
import {
  Activity,
  AlertCircle,
  BarChart2,
  CalendarClock,
  ChromeIcon,
  Eye,
  Globe,
  Layers,
  LinkIcon,
  MapPin,
  Monitor,
  MousePointerClick,
  Smartphone,
  Users,
} from "lucide-react";
import { PlanGate } from "@/components/billing/PlanGate";
import { PageHeader } from "@/components/PageHeader";
import { BarChart, AxisLabels } from "@/components/dashboard/BarChart";
import { AnalyticsTabs, ChartSkeleton, EmptyChart, MetricCard, PeriodPicker, SectionCard, fmt } from "@/components/analytics/shared";
import { BreakdownDonut, ChannelSeriesChart, ClickHeatmap, PlatformComparison, RankedList, TrendLine } from "@/components/analytics/charts";
import { OptOutToggle } from "@/components/analytics/OptOutToggle";
import { useClickAnalytics } from "@/lib/hooks/useClickAnalytics";
import {
  DEVICE_LABELS,
  resolveAnalyticsRange,
  shortDayLabel,
  type AnalyticsSelection,
  type ClickAnalytics,
} from "@/lib/analytics";

function TimelineCards({ data, isLoading }: { readonly data: ClickAnalytics | undefined; readonly isLoading: boolean }): JSX.Element {
  const daily = data?.daily ?? [];
  const labels = daily.map((d) => shortDayLabel(d.day));
  const views = daily.map((d) => d.views);
  const visitors = daily.map((d) => d.visitors);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
      <SectionCard title="Visualizações" subtitle="cliques por dia" icon={<Eye size={14} />}>
        {isLoading ? (
          <ChartSkeleton />
        ) : views.some((v) => v > 0) ? (
          <div className="pt-2">
            <BarChart values={views} labels={labels} className="h-20" color="hsl(42 80% 57%)" />
            <AxisLabels labels={labels} />
          </div>
        ) : (
          <EmptyChart />
        )}
      </SectionCard>
      <SectionCard title="Visitantes" subtitle="visitantes únicos por dia" icon={<Users size={14} />}>
        {isLoading ? (
          <ChartSkeleton />
        ) : visitors.some((v) => v > 0) ? (
          <div className="pt-2">
            <TrendLine values={visitors} color="hsl(150 54% 51%)" label="Visitantes únicos por dia" className="h-20" />
            <AxisLabels labels={labels} />
          </div>
        ) : (
          <EmptyChart />
        )}
      </SectionCard>
    </div>
  );
}

function AudienceCards({ data }: { readonly data: ClickAnalytics }): JSX.Element {
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
      <SectionCard title="Dispositivos" icon={<Monitor size={14} />}>
        <BreakdownDonut items={data.devices} labelMap={DEVICE_LABELS} emptyMessage="Sem cliques com dispositivo identificado." />
      </SectionCard>
      <SectionCard title="Navegadores" icon={<ChromeIcon size={14} />}>
        <BreakdownDonut items={data.browsers} emptyMessage="Sem cliques com navegador identificado." />
      </SectionCard>
      <SectionCard title="Sistema operacional" icon={<Smartphone size={14} />}>
        <BreakdownDonut items={data.oses} emptyMessage="Sem cliques com sistema identificado." />
      </SectionCard>
    </div>
  );
}

function LocationCards({ data }: { readonly data: ClickAnalytics }): JSX.Element {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <SectionCard title="Cidades" icon={<MapPin size={14} />}>
        <RankedList
          items={data.cities.map((c) => ({ key: `${c.label}-${c.region ?? ""}`, label: c.label, detail: c.region, count: c.count }))}
          total={data.views}
          emptyMessage="Nenhuma cidade registrada ainda."
        />
      </SectionCard>
      <SectionCard title="Países" icon={<Globe size={14} />}>
        <RankedList items={data.countries.map((c) => ({ key: c.label, label: c.label, count: c.count }))} total={data.views} emptyMessage="Nenhum país registrado ainda." />
      </SectionCard>
      <SectionCard title="Fontes de tráfego" icon={<LinkIcon size={14} />}>
        <RankedList items={data.referrers.map((r) => ({ key: r.label, label: r.label, count: r.count }))} total={data.views} emptyMessage="Nenhuma fonte de tráfego registrada." />
      </SectionCard>
    </div>
  );
}

function ChannelCards({ data }: { readonly data: ClickAnalytics }): JSX.Element {
  const days = data.daily.map((d) => d.day);
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
      <SectionCard title="Cliques por canal" subtitle="grupo do Telegram ou origem do link" icon={<BarChart2 size={14} />} className="lg:col-span-3">
        <ChannelSeriesChart series={data.channelSeries} days={days} />
      </SectionCard>
      <SectionCard title="Comparativo de plataformas" subtitle="cliques x links criados no período" icon={<Layers size={14} />} className="lg:col-span-2">
        <PlatformComparison rows={data.platforms} />
      </SectionCard>
    </div>
  );
}

function AnalyticsPageContent(): JSX.Element {
  const [selection, setSelection] = useState<AnalyticsSelection>({ preset: "30d" });
  const range = useMemo(() => resolveAnalyticsRange(selection), [selection]);
  const { data, isLoading, error } = useClickAnalytics(range);

  return (
    <div className="space-y-6 max-w-6xl">
      <PageHeader
        title="Analytics"
        subtitle="Cliques dos seus links, sem cookies de rastreio."
        actions={
          <div className="flex flex-col items-end gap-2">
            <PeriodPicker value={selection} onChange={setSelection} invalid={range === null} />
            <OptOutToggle />
          </div>
        }
      />
      <AnalyticsTabs active="overview" />

      {error && (
        <div role="alert" className="flex items-center gap-3 rounded-lg border border-[hsl(var(--destructive)/0.3)] bg-[hsl(var(--destructive)/0.08)] px-4 py-3 text-sm text-[hsl(var(--destructive))]">
          <AlertCircle size={16} className="shrink-0" />
          <span>Não foi possível carregar o Analytics: {error.message}</span>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard label="Visualizações" value={fmt(data?.views ?? 0)} icon={<Eye size={14} />} subtitle="cliques no período" isLoading={isLoading} />
        <MetricCard label="Visitantes" value={fmt(data?.uniqueVisitors ?? 0)} icon={<Users size={14} />} subtitle="únicos no período" isLoading={isLoading} />
        <MetricCard
          label="Cliques por visitante"
          value={data && data.uniqueVisitors > 0 ? (data.views / data.uniqueVisitors).toLocaleString("pt-BR", { maximumFractionDigits: 1 }) : "---"}
          icon={<MousePointerClick size={14} />}
          subtitle="média no período"
          isLoading={isLoading}
        />
        <MetricCard
          label="Taxa de rejeição"
          value={data?.bounceRate != null ? `${data.bounceRate}%` : "---"}
          icon={<Activity size={14} />}
          subtitle="estimada: visitantes de 1 clique"
          isLoading={isLoading}
        />
      </div>

      <TimelineCards data={data} isLoading={isLoading} />

      <SectionCard title="Melhor horário para divulgar" subtitle="cliques por dia da semana e hora" icon={<CalendarClock size={14} />}>
        {isLoading || !data ? <ChartSkeleton /> : <ClickHeatmap cells={data.heatmap} />}
      </SectionCard>

      {data && (
        <>
          <ChannelCards data={data} />
          <AudienceCards data={data} />
          <LocationCards data={data} />
        </>
      )}
    </div>
  );
}

/** Analytics: recurso PRO. O PlanGate so esconde a tela; quem barra e o banco:
 *  get_click_analytics/get_member_analytics chamam require_full_analytics
 *  (plan_has_feature 'full_analytics') e respondem plan_required no Free. */
export function AnalyticsPage(): JSX.Element {
  return (
    <PlanGate feature="fullAnalytics" title="Analytics" subtitle="Cliques, visitantes e canais que mais vendem.">
      <AnalyticsPageContent />
    </PlanGate>
  );
}
